import { PriceEvolutionDataItem, PriceEvolutionResponse } from '@/models/queries/getPriceEvolution';
import { PropertyType } from '@/types';
import maxBy from 'lodash.maxby';
import minBy from 'lodash.minby';

export interface PriceTrendsChartDataItem {
  series: ApexAxisChartSeries;
  categories: string[];
  evolutions: number[];
  min: number;
  max: number;
}

export interface PriceTrendsChartData {
  [PropertyType.Apartment]: PriceTrendsChartDataItem | null;
  [PropertyType.House]: PriceTrendsChartDataItem | null;
}

export const getEvolution = (factorValue: number, previousFactorValue: number): number =>
  parseFloat(((factorValue / previousFactorValue - 1) * 100).toFixed(2));

const getSeriesIs24 = (data: Array<PriceEvolutionDataItem>): ApexAxisChartSeries => {
  if (data.length === 0) {
    return [{ data: [] }];
  }
  const initialFactorValue = data[0].factorValue;
  const seriesData = data.map((item) => getEvolution(item.factorValue, initialFactorValue));
  return [
    {
      data: seriesData,
    },
  ];
};

const getSeriesHg = (data: Array<PriceEvolutionDataItem>): ApexAxisChartSeries => {
  if (data.length === 0) {
    return [{ data: [] }];
  }
  const seriesData = data.map((item) => item.priceValue);
  return [
    {
      data: seriesData,
    },
  ];
};

const getEvolutionsHg = (data: Array<PriceEvolutionDataItem>): number[] => {
  if (data.length === 0) {
    return [];
  }
  const initialFactorValue = data[0].factorValue;
  return data.map((item) => getEvolution(item.factorValue, initialFactorValue));
};

const getCategories = (data: Array<PriceEvolutionDataItem>): string[] =>
  data.map((item, index) => {
    const year = Math.round(item.timePeriod / 100) + 1;
    const isYearNode = item.timePeriod % 100 === 12; // Pick: 202212, 202312,... Non-pick: 202206, 202306,...
    return isYearNode ? year.toString() : '';
  });

const roundNumbers = (dataArray: number[]) => dataArray.map((item) => Math.round(item / 100) * 100);

const getMinIs24 = (data: number[]): number => Math.floor(minBy(data) ?? 0);
const getMinHg = (data: number[]): number =>
  Math.floor((minBy(roundNumbers(data)) ?? 0) / 100) * 100 - 100;

const getMaxIs24 = (data: number[]): number => Math.ceil(maxBy(data) ?? 0);
const getMaxHg = (data: number[]): number =>
  Math.ceil((maxBy(roundNumbers(data)) ?? 0) / 100) * 100;

const adaptData = (
  data: PriceEvolutionResponse,
  range: number,
  isIs24: boolean,
): PriceTrendsChartData => {
  const sliceSize = range === 0 ? 0 : (range * 2 + 1) * -1;
  const results = data?.results;

  const processResult = (propertyType: PropertyType): PriceTrendsChartDataItem => {
    const result = (results?.[propertyType] ?? []).slice(sliceSize);
    const series = isIs24 ? getSeriesIs24(result) : getSeriesHg(result);

    return {
      series,
      categories: getCategories(result),
      min: isIs24 ? getMinIs24(series[0].data as number[]) : getMinHg(series[0].data as number[]),
      max: isIs24 ? getMaxIs24(series[0].data as number[]) : getMaxHg(series[0].data as number[]),
      evolutions: isIs24 ? [] : getEvolutionsHg(result),
    };
  };

  return {
    [PropertyType.Apartment]: processResult(PropertyType.Apartment),
    [PropertyType.House]: processResult(PropertyType.House),
  };
};

export default adaptData;
